import {
    USER_SEGMENTATION_TEAM_TYPE_DESIGN,
    USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE
} from "@atlassian/onboarding-survey";

import { JiraFFLContentGenerationResponse } from "@/libraries/bxp-web-stargate-client/src";

import {
    generateSequentialDueDates,
    getElapsedDaysFromNow
} from "../../ondemand/signup/confirmation/confirmation-page/src/views/setup/jira/utils";
import { type TableRowProps } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-preview-modal/preview-table/table-row";

export const ANALYTIC_EVENT_ACTION_SUCCEEDED = "succeeded";
export const ANALYTIC_EVENT_ACTION_STARTED = "started";
export const ANALYTIC_EVENT_ACTION_FAILED = "failed";
export const MADLIB_CONTENT_GENERATION_ANALYTIC_EVENT_NAME =
    "madlib content generation";
export const MADLIB_FLOW = "madlib";

export const teamTypesMap = {
    [USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: {
        messageKey: "softwareDevelopment"
    },
    [USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT]: {
        messageKey: "productManagement"
    },
    [USER_SEGMENTATION_TEAM_TYPE_MARKETING]: {
        messageKey: "marketing"
    },
    [USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT]: {
        messageKey: "projectManagement"
    },
    [USER_SEGMENTATION_TEAM_TYPE_DESIGN]: {
        messageKey: "design"
    },
    [USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: {
        messageKey: "it"
    }
} as const;

export type TeamType = keyof typeof teamTypesMap;

export type AnchorConfig = {
    teamType: TeamType;
    suggestionIndex: number;
};

const anchorConfigMap: Partial<Record<string, AnchorConfig>> = {
    "Project Management": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
        suggestionIndex: 1
    },
    "Project Collaboration": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
        suggestionIndex: 1
    },
    "Agile": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
        suggestionIndex: 8
    },
    "Template": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
        suggestionIndex: 1
    },
    "Roadmaps": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
        suggestionIndex: 0
    },
    // "Timeline": {
    //     teamType: USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    //     suggestionIndex: 0
    // },
    "Automation": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
        suggestionIndex: 1
    },
    "Tracker": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
        suggestionIndex: 6
    },
    "Teamwork": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
        suggestionIndex: 1
    },
    "Competitors": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
        suggestionIndex: 0
    },
    "Marketing": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_MARKETING,
        suggestionIndex: 0
    },
    "Software Engineering": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
        suggestionIndex: 0
    },
    "Design": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_DESIGN,
        suggestionIndex: 2
    },
    "Program Management": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
        suggestionIndex: 1
    },
    "Sales": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_MARKETING,
        suggestionIndex: 2
    },
    "HR": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
        suggestionIndex: 0
    },
    "Legal": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
        suggestionIndex: 0
    },
    "Operations": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
        suggestionIndex: 1
    }
};

const competitorsSubAnchorConfigMap: Partial<Record<string, AnchorConfig>> = {
    "Azure DevOps": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
        suggestionIndex: 8
    },
    "Linear": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
        suggestionIndex: 8
    }
};

const trackerSubAnchorConfigMap: Partial<Record<string, AnchorConfig>> = {
    "Bug Tracker": {
        teamType: USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
        suggestionIndex: 9
    }
};

export const getAnchorConfig = (
    anchor?: string,
    subAnchor?: string
): AnchorConfig | undefined => {
    if (!anchor) {
        return undefined;
    }

    if (anchor === "Tracker" && subAnchor) {
        return trackerSubAnchorConfigMap[subAnchor] ?? anchorConfigMap[anchor];
    }

    if (anchor === "Competitors" && subAnchor) {
        return (
            competitorsSubAnchorConfigMap[subAnchor] ?? anchorConfigMap[anchor]
        );
    }

    return anchorConfigMap[anchor];
};

export const getTeamTypeFromAnchor = (
    anchor?: string,
    subAnchor?: string
): TeamType | undefined => getAnchorConfig(anchor, subAnchor)?.teamType;

export const getMessageKeyForTeamType = (teamType: TeamType) =>
    teamTypesMap[teamType].messageKey;

const DEFAULT_END_DATE_DAYS_FROM_NOW = 30;

export const formatCgsData = (
    cgsData: JiraFFLContentGenerationResponse | undefined
): Omit<TableRowProps, "isLastRow">[] | undefined => {
    if (!cgsData) {
        return undefined;
    }

    const dueDate =
        Date.now() + DEFAULT_END_DATE_DAYS_FROM_NOW * 24 * 60 * 60 * 1000;
    const relativeDays = getElapsedDaysFromNow(dueDate);
    const taskPriorities: TableRowProps["priority"][] = [
        "medium",
        "low",
        "low",
        "low",
        "medium",
        "medium",
        "low",
        "medium",
        "medium",
        "low"
    ];
    const assignees = [
        "Jenna Tran",
        "Raul Gonzales",
        "Aoife Burke",
        "Jenna Tran",
        "Omar Salah",
        "Bradley Philips",
        "Raul Gonzales",
        "Aoife Burke",
        "Bradley Philips",
        "Athena Ai"
    ];
    const assigneeAvatars = {
        "Aoife Burke":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXB.webp",
        "Athena Ai":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXE.webp",
        "Bradley Philips":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXC.webp",
        "Jenna Tran":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXF.webp",
        "Omar Salah":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXG.webp",
        "Raul Gonzales":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXD.webp"
    };

    const epicData = cgsData.sampleIssues.epic;
    const workItemsData = cgsData.sampleIssues.childWorkItems;
    const finalData: Omit<TableRowProps, "isLastRow">[] = [];
    if (epicData) {
        const epicDueDate = new Date();
        epicDueDate.setDate(epicDueDate.getDate() + relativeDays);
        const assignee = assignees.shift() || (assignees[0] as string);

        finalData.push({
            type: "epic",
            summary: epicData.epicName,
            status: "in progress",
            dueDate: epicDueDate,
            priority: "high",
            assignee,
            assigneeImgSrc:
                assigneeAvatars[assignee as keyof typeof assigneeAvatars]
        });
    }

    if (workItemsData && workItemsData.length > 0) {
        const issueDueDates = generateSequentialDueDates(
            dueDate,
            workItemsData.length
        );
        const issueDueDateRelativeDays = issueDueDates.map((issueDueDate) =>
            getElapsedDaysFromNow(new Date(issueDueDate).getTime())
        );
        workItemsData.forEach((item, index) => {
            const taskDueDate = new Date();
            taskDueDate.setDate(
                taskDueDate.getDate() +
                    (issueDueDateRelativeDays[index] ?? relativeDays)
            );
            const assignee = assignees.shift() || (assignees[0] as string);

            finalData.push({
                type: "task",
                summary: item.summary,
                status: "to do",
                dueDate: taskDueDate,
                priority: taskPriorities.shift() || "medium",
                assignee,
                assigneeImgSrc:
                    assigneeAvatars[assignee as keyof typeof assigneeAvatars]
            });
        });
    }

    return finalData;
};

// fallback data for when api request to csg fails
export const fallbackCgsData: JiraFFLContentGenerationResponse = {
    cacheKey: "default",
    sampleIssues: {
        childWorkItems: [
            {
                summary: "Identify and categorize project stakeholders"
            },
            {
                summary: "Draft project kickoff meeting agenda"
            },
            {
                summary: "Schedule and facilitate project kickoff session"
            },
            {
                summary: "Define communication channels and reporting cadence"
            },
            {
                summary: "Distribute kickoff meeting minutes and action items"
            }
        ],
        epic: {
            description:
                "What are we doing? We are initiating the project kickoff process to ensure all relevant parties are identified and engaged from the start. Why are we doing it? The primary goal is to align stakeholders on project objectives, scope, and expectations to ensure a smooth project execution. What does success look like? Success is achieved when all stakeholders are identified, a kickoff meeting is conducted, and there is a documented consensus on the project direction.",
            epicName: "Project kickoff and stakeholder alignment"
        },
        spaceName: "Project Management Office"
    }
};
